import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import cornerstoneMath from 'cornerstone-math';
import { historyRef } from '../../routes';
import { hotkeysManager } from '../../App';
import store from './../../store';
import {
  setActiveMarkUUID,
  setPagination,
  setActiveContextMenuType,
  getMarkPermissions,
} from '@ohif/viewer/src/store/mark/action';
import { hasPermissionSelector } from '@ohif/viewer/src/store/permission/selector';
import { setActiveButtonIds } from '@ohif/viewer/src/store/viewer/actions';
import MarkRectangleRoiTool from '../tools/MarkRectangleRoiTool';
import ToolContextMenu from './toolContextMenu';
import MarkFreehandRoiTool from '../tools/MarkFreehandRoiTool';

// 允许的标注工具集
const allowTools = ['MarkFreehandRoi', 'MarkRectangleRoi'];

// 获取最后一个工具记录
const getLastToolData = () => {
  const toolHistory = cornerstoneTools.store.state.globalToolChangeHistory;
  return toolHistory[toolHistory.length - 1];
};

// 获取最后一个工具实例
const getLastToolInstance = element => {
  const toolData = getLastToolData();
  const toolName = toolData.args[0];
  return cornerstoneTools.getToolForElement(element, toolName);
};

// 当前是否为FreehandRoi工具
const isActiveTool = toolName => {
  const toolNames = Array.isArray(toolName) ? toolName : [toolName];
  const toolData = getLastToolData();
  return toolNames.includes(toolData.args[0]) && toolData.mode === 'active';
};

function pointNearHandle(element, data, coords) {
  if (data.handles === undefined || data.handles.points === undefined) {
    return;
  }

  if (data.visible === false) {
    return;
  }

  for (let i = 0; i < data.handles.points.length; i++) {
    const handleCanvas = cornerstone.pixelToCanvas(
      element,
      data.handles.points[i]
    );

    if (cornerstoneMath.point.distance(handleCanvas, coords) < 6) {
      return i;
    }
  }
}

function pointNearHandleAllTools(detail) {
  const { currentPoints, element } = detail;
  const coords = currentPoints.canvas;
  const toolState = cornerstoneTools.getToolState(element, 'MarkFreehandRoi');
  if (!toolState) {
    return;
  }
  let handleNearby;
  for (let toolIndex = 0; toolIndex < toolState.data.length; toolIndex++) {
    handleNearby = pointNearHandle(element, toolState.data[toolIndex], coords);
    if (handleNearby !== undefined) {
      return {
        handleNearby,
        toolIndex,
      };
    }
  }
}

// 删除
function deletePoint(eventData, deleteInfo) {
  const toolData = cornerstoneTools.getToolState(
    eventData.element,
    'MarkFreehandRoi'
  );

  if (toolData === undefined) {
    return;
  }

  const deleteHandle = deleteInfo.handleIndex;
  const toolIndex = deleteInfo.toolIndex;

  // Get the toolData from insertInfo
  const data = toolData.data[toolIndex];

  const points = data.handles.points;

  // Only allow delete if > 3 points
  if (points.length <= 3) {
    return;
  }

  // Link the line of the previous handle to the one after handles[deleteHandle];
  if (deleteHandle === points.length - 1) {
    points[deleteHandle - 1].lines.pop();
    points[deleteHandle - 1].lines.push(points[0]);
  } else if (deleteHandle === 0) {
    points[points.length - 1].lines.pop();
    points[points.length - 1].lines.push(points[deleteHandle + 1]);
  } else {
    points[deleteHandle - 1].lines.pop();
    points[deleteHandle - 1].lines.push(points[deleteHandle + 1]);
  }

  // Remove the handle
  points.splice(deleteHandle, 1);

  data.invalidated = true;
  data.active = true;
  data.highlight = true;

  // Force onImageRendered to fire
  cornerstone.updateImage(eventData.element);
}

export default ({ commandsManager, servicesManager }) => {
  cornerstoneTools.addTool(MarkRectangleRoiTool);
  cornerstoneTools.setToolEnabled('MarkRectangleRoi', {
    mouseButtonMask: 1,
  });
  cornerstoneTools.addTool(MarkFreehandRoiTool);
  cornerstoneTools.setToolEnabled('MarkFreehandRoi', {
    mouseButtonMask: 1,
  });

  let caseId, menuType;

  const { SidePanelService, UIDialogService } = servicesManager.services;
  // 初始化界面时
  cornerstone.events.addEventListener(
    cornerstone.EVENTS.ELEMENT_ENABLED,
    event => {
      // 获取权限
      caseId =
        historyRef.current.location.search.match(/aiCheckId=(\d+)/) || [];
      caseId = caseId[1];
      menuType = historyRef.current.location.search.match(/type=(\d+)/) || [];
      menuType = menuType[1];
      store.dispatch(getMarkPermissions(caseId, menuType));

      const { SidePanelService } = servicesManager.services;
      event.detail.element.addEventListener(
        cornerstoneTools.EVENTS.MOUSE_CLICK,
        evt => {
          const { detail } = evt;
          const nearby = pointNearHandleAllTools(detail);
          if (detail.event.ctrlKey && detail.event.button === 2) {
            if (nearby && nearby.handleNearby !== null) {
              const deleteInfo = {
                toolIndex: nearby.toolIndex,
                handleIndex: nearby.handleNearby,
              };

              deletePoint(detail, deleteInfo);
            }
          }
        }
      );

      // 右键撤销
      event.detail.element.addEventListener('contextmenu', () => {
        if (!isActiveTool(allowTools)) return;
        const { element } = event.detail;
        const toolInstance = getLastToolInstance(element);
        if (!toolInstance) return console.warn('未找到当前使用的工具!');
        const activeDrawing = toolInstance._activeDrawingToolReference;
        if (
          activeDrawing &&
          activeDrawing.handles &&
          (activeDrawing.handles.points || []).length > 1
        ) {
          const configuration = toolInstance._configuration;
          if (configuration.currentHandle > 1) {
            const len = activeDrawing.handles.points.length;
            activeDrawing.handles.points.length = len - 1;
            activeDrawing.handles.points[len - 2].lines = [];
            configuration.currentHandle = configuration.currentHandle - 1;
            cornerstone.updateImage(element);
          }
        }
      });

      // 右键菜单
      event.detail.element.addEventListener(
        cornerstoneTools.EVENTS.MOUSE_CLICK,
        ({ detail }) => {
          const { selectedRightSidePanel } = SidePanelService.getSidePanelKey();
          // 绘图状态不打开菜单
          if (
            cornerstoneTools.store.state.isMultiPartToolActive ||
            selectedRightSidePanel === 'mark-form'
          )
            return;
          const hasPermission = hasPermissionSelector(store.getState());
          const isMarkEdit = hasPermission({
            menu: 'markTask_' + menuType,
            action: 'showButton',
            data: 'markTask_' + caseId,
          });
          if (!isMarkEdit) return;

          const mouseUpEvent = detail.event;
          const isRightClick = mouseUpEvent.which === 3;
          if (!isRightClick) return;

          const _getDefaultPosition = event => ({
            x: (event && event.currentPoints.client.x) || 0,
            y: (event && event.currentPoints.client.y) || 0,
          });
          const toolStateManager =
            cornerstoneTools.globalImageIdSpecificToolStateManager.toolState[
              detail.image.imageId
            ];
          const data = Object.values(toolStateManager)
            .reduce((acc, { data }) => acc.concat(data), [])
            .find(item => item.active);
          store.dispatch(setActiveMarkUUID(data.uuid));
          UIDialogService.dismiss({ id: 'contextMenu' });
          UIDialogService.create({
            id: 'contextMenu',
            isDraggable: false,
            preservePosition: false,
            defaultPosition: _getDefaultPosition(detail),
            content: ToolContextMenu,
            contentProps: {
              eventData: detail,
              onEdit: () => {
                store.dispatch(setActiveContextMenuType('edit'));
              },
              onDelete: () => {
                store.dispatch(setActiveContextMenuType('remove'));
              },
              onClose: () => UIDialogService.dismiss({ id: 'contextMenu' }),
            },
          });
        }
      );

      // 绘图后操作
      event.detail.element.addEventListener(
        cornerstoneTools.EVENTS.MEASUREMENT_COMPLETED,
        ({ detail }) => {
          if (!isActiveTool(allowTools)) return;
          const { toolType, measurementData } = detail;
          const { uuid } = measurementData;
          // 判断是否为已存在标注,用于过滤鼠标点击绘图导致触发编辑事件
          if (typeof measurementData.currentPage === 'number') {
            store.dispatch(setActiveMarkUUID(uuid));
            store.dispatch(
              setPagination({ current: measurementData.currentPage + 1 })
            );
            return;
          }
          if (!isActiveTool(allowTools)) return;
          // 打开标注编辑页面
          SidePanelService.setState({ toolType, uuid });
          const { selectedRightSidePanel } = SidePanelService.getSidePanelKey();
          if (selectedRightSidePanel !== 'mark-form')
            SidePanelService.handleSidePanelChange('right', 'mark-form');
        }
      );

      // 多边形绘图间距
      event.detail.element.addEventListener(
        cornerstoneTools.EVENTS.MOUSE_DOWN,
        ({ detail }) => {
          const toolType = 'MarkFreehandRoi';
          if (!isActiveTool(toolType)) return;
          const { ctrlKey } = detail.event;
          let spacing = ctrlKey
            ? localStorage.getItem('freehand-doubleSpacing')
            : localStorage.getItem('freehand-spacing');
          spacing = parseInt(spacing);
          if (!spacing) return;
          const toolInstance = getLastToolInstance(detail.element);
          toolInstance.configuration.spacing = spacing;
        }
      );
    }
  );

  const definitions = {
    markTool: {
      commandFn: ({ toolName }) => {
        const hasPermission = hasPermissionSelector(store.getState());
        const isMarkEdit = hasPermission({
          menu: 'markTask_' + menuType,
          action: 'showButton',
          data: 'markTask_' + caseId,
        });

        if (!isMarkEdit)
          return setTimeout(() => store.dispatch(setActiveButtonIds()));
        cornerstoneTools.setToolActive(toolName, {
          mouseButtonMask: 1,
        });

        /******lhwen add  新增标注功能热键设置********************************************/
        if (toolName === 'MarkFreehandRoi' || toolName === 'MarkRectangleRoi') {
          const markhotkeys = JSON.parse(
            localStorage.getItem('hotkey-mark-definitions') ||
              '{"markNextImage":{"keys":["down"],"label":"Next Image","type":"mark"},"markPreviousImage":{"keys":["up"],"label":"Previous Image","type":"mark"},"markScaleDownViewport":{"keys":["-"],"label":"Zoom Out","type":"mark"},"markScaleUpViewport":{"keys":["+"],"label":"Zoom In","type":"mark"},"markWindowLevelPreset1":{"keys":["1"],"label":"W/L W+","type":"mark"},"markWindowLevelPreset2":{"keys":["2"],"label":"W/L W-","type":"mark"},"markWindowLevelPreset3":{"keys":["3"],"label":"W/L L+","type":"mark"},"markWindowLevelPreset4":{"keys":["4"],"label":"W/L L+","type":"mark"}}'
          );
          hotkeysManager.setHotkeys(markhotkeys);
        }

        /************************************************ end */

        //设置标注手柄
        // const { viewports } = store.getState();
        // const element =
        //   cornerstoneTools.store.state.enabledElements[
        //   viewports.activeViewportIndex
        //   ];
        // 打开右侧面板
        const { isRightSidePanelOpen } = SidePanelService.getSidePanelState();
        if (!isRightSidePanelOpen)
          SidePanelService.handleSidePanelChange('right', 'mark-list');

        // 中心化窗口(仅显示1个窗口)
        commandsManager.runCommand('setCornerstoneLayout');
      },
      storeContexts: ['viewports'],
    },
    // 取消绘图
    cancelDrawing: {
      commandFn: ({ viewports, evt }) => {
        const element =
          cornerstoneTools.store.state.enabledElements[
            viewports.activeViewportIndex
          ];
        const toolInstance = getLastToolInstance(element);

        if (toolInstance && toolInstance.cancelDrawing)
          toolInstance.cancelDrawing(element, evt);
      },
      storeContexts: ['viewports'],
    },
    // 闭合绘图
    completeDrawing: {
      commandFn: ({ viewports }) => {
        const element =
          cornerstoneTools.store.state.enabledElements[
            viewports.activeViewportIndex
          ];
        const toolInstance = getLastToolInstance(element);
        if (toolInstance && toolInstance.completeDrawing)
          toolInstance.completeDrawing(element);
      },
      storeContexts: ['viewports'],
    },
  };
  return {
    definitions,
    defaultContext: 'ACTIVE_VIEWPORT::CORNERSTONE',
  };
};
